<script setup lang="ts">
import { ref } from 'vue';

/**
 * Text input — relies on parent .form scope for styling.
 * v-model compatible; emits change/blur for validation hooks.
 */
type InputType = 'text' | 'email' | 'password' | 'number' | 'date' | 'datetime-local';

withDefaults(
  defineProps<{
    type?: InputType;
    placeholder?: string;
    autocomplete?: string;
    required?: boolean;
    disabled?: boolean;
    readonly?: boolean;
    invalid?: boolean;
    autofocus?: boolean;
    name?: string;
    inputmode?: 'text' | 'numeric' | 'email' | 'decimal' | 'tel' | 'search' | 'url';
    min?: string | number;
    max?: string | number;
    step?: string | number;
  }>(),
  {
    type: 'text',
    placeholder: undefined,
    autocomplete: undefined,
    required: false,
    disabled: false,
    readonly: false,
    invalid: false,
    autofocus: false,
    name: undefined,
    inputmode: undefined,
    min: undefined,
    max: undefined,
    step: undefined,
  },
);

const model = defineModel<string>({ default: '' });

const inputRef = ref<HTMLInputElement | null>(null);

defineExpose({
  focus: () => inputRef.value?.focus(),
});
</script>

<template>
  <input
    ref="inputRef"
    v-model="model"
    :type="type"
    :placeholder="placeholder"
    :autocomplete="autocomplete"
    :required="required"
    :disabled="disabled"
    :readonly="readonly"
    :aria-invalid="invalid || undefined"
    :autofocus="autofocus"
    :name="name"
    :inputmode="inputmode"
    :min="min"
    :max="max"
    :step="step"
  />
</template>
